import React, { useState } from 'react';
import { Edit2, Users, Trash2, Calendar, MapPin, Search, ChevronDown, ChevronUp, Clock, AlertTriangle, MessageCircle, AlertCircle, Wrench, Coffee, ClipboardList, Upload, Camera, Star } from 'lucide-react';
import EditGame from './EditGame';
import SquadSelection from './SquadSelection';
import WhatsAppGroupMessenger from './WhatsAppGroupMessenger';
import { formatDate, getGameStatus, formatTime } from '../../utils/helpers';
import ImportScheduleModal from './ImportScheduleModal';
import MatchRecapModal from '../shared/MatchRecapModal';
import MediaUploadModal from '../shared/MediaUploadModal';
import MediaGalleryPreview from '../shared/MediaGalleryPreview';

// Responsibility configuration - keys must match AVAILABLE_DUTIES in SquadSelection
const RESPONSIBILITIES = {
  ground_setup: { label: 'Ground Setup', icon: Wrench, color: 'orange' },
  refreshments: { label: 'Drinks & Snacks', icon: Coffee, color: 'blue' },
  scoring: { label: 'Scoring', icon: ClipboardList, color: 'green' },
  umpiring: { label: 'Umpiring', icon: ClipboardList, color: 'purple' },
  first_aid: { label: 'First Aid', icon: ClipboardList, color: 'red' },
  equipment: { label: 'Equipment', icon: ClipboardList, color: 'gray' },
  transport: { label: 'Transport', icon: ClipboardList, color: 'teal' },
  photography: { label: 'Photography', icon: ClipboardList, color: 'pink' }
};

// Dynamic late fee amount is used per game

const GamesList = ({
  games,
  players,
  onBack,
  onAddGame,
  onEditGame,
  onDeleteGame,
  onSelectPlaying11,
  onSendGameReminder,
  onSendAvailabilityReminder,
  onViewAvailability,
  onMarkLateArrival,
  onAssignDuty,
  teams,
  tournaments,
  onImportGames,
  onUpdateGameStatus,
  onSaveMatchRecap,
  isAdmin = false
}) => {
  const [expandedGame, setExpandedGame] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingDuty, setEditingDuty] = useState(null); // { gameId, dutyId } - which duty is being edited
  const [editingRecapGame, setEditingRecapGame] = useState(null);
  const [uploadingMediaGame, setUploadingMediaGame] = useState(null);
  const [selectedTournament, setSelectedTournament] = useState('all');

  // Check if game date is today or in the past
  const isGameDayOrPast = (gameDate) => {
    if (!gameDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const gDate = new Date(gameDate);
    gDate.setHours(0, 0, 0, 0);
    return gDate <= today;
  };

  // Helper to get player name by ID
  const getPlayerName = (playerId) => {
    const player = players?.find(p =>
      p.id === playerId ||
      p.oderId === playerId ||
      p.oderId === playerId ||
      p.userId === playerId ||
      p.email === playerId // Sometimes email is used as ID
    );
    return player?.name || 'Unknown';
  };

  // Helper to get players with a specific responsibility/duty
  const getPlayersWithResponsibility = (game, responsibilityId) => {
    // Check both playerDuties (new format) and playerResponsibilities (old format)
    const duties = game.playerDuties || {};
    const responsibilities = game.playerResponsibilities || {};

    // From playerDuties (single duty per player)
    const playersFromDuties = Object.entries(duties)
      .filter(([playerId, dutyId]) => dutyId === responsibilityId)
      .map(([playerId]) => getPlayerName(playerId));

    // From playerResponsibilities (array of duties per player - legacy)
    const playersFromResponsibilities = Object.entries(responsibilities)
      .filter(([playerId, resps]) => Array.isArray(resps) && resps.includes(responsibilityId))
      .map(([playerId]) => getPlayerName(playerId));

    // Combine and deduplicate
    return [...new Set([...playersFromDuties, ...playersFromResponsibilities])];
  };

  const filteredGames = games.filter(game => {
    if (selectedTournament === 'all') return true;
    return game.tournamentId === selectedTournament || game.tournament === selectedTournament;
  });

  const gamesByTournament = filteredGames.reduce((acc, game) => {
    const tName = game.tournamentName || 'Independent Games';
    if (!acc[tName]) acc[tName] = { active: [], completed: [] };

    const status = getGameStatus(game);
    const isCompleted = status === 'Completed' || status === 'Abandoned' || status === 'Cancelled';

    if (isCompleted) {
      acc[tName].completed.push(game);
    } else {
      acc[tName].active.push(game);
    }
    return acc;
  }, {});

  return (
    <div className="max-w-4xl mx-auto">
      <button onClick={onBack} className="text-gray-600 mb-4 hover:text-gray-800">
        ← Back
      </button>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Games</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 font-semibold"
          >
            <Upload size={18} />
            Import Schedule
          </button>
          <button
            onClick={onAddGame}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-semibold"
          >
            + Add Game
          </button>
        </div>
      </div>

      {tournaments && tournaments.length > 0 && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-gray-600 font-medium">Filter by Tournament:</span>
          <select
            value={selectedTournament}
            onChange={(e) => setSelectedTournament(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 max-w-xs"
          >
            <option value="all">All Games</option>
            {tournaments.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="space-y-4">
        {filteredGames.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-gray-500 text-lg mb-2">No games found</p>
            <p className="text-gray-400 text-sm">Try adjusting your filters or create a new game</p>
          </div>
        ) : (
          (() => {
            const renderGameCard = (game) => {
              const isExpanded = expandedGame === game.id;
              const tournament = tournaments?.find(t => t.name === game.tournamentName || t.id === game.tournamentId);
              
              const maxSquadSize = game.maxSquadSize !== undefined && game.maxSquadSize !== null 
                ? game.maxSquadSize 
                : (tournament?.maxSquadSize !== undefined && tournament?.maxSquadSize !== null ? tournament.maxSquadSize : 11);
                
              const hasSquad = maxSquadSize > 0 
                ? (game.playing11?.length || 0) >= maxSquadSize 
                : (game.playing11?.length || 0) > 0;
                
              const isPast = new Date(game.date) < new Date(new Date().setHours(0, 0, 0, 0));
              // Check both formats for duties
              const hasResponsibilities = (
                (game.playerDuties && Object.keys(game.playerDuties).length > 0) ||
                (game.playerResponsibilities && Object.values(game.playerResponsibilities).some(r => r?.length > 0))
              );

              return (
                <div key={game.id} className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden ${getGameStatus(game) === 'Cancelled' ? 'opacity-75 border-l-4 border-l-red-500' :
                  getGameStatus(game) === 'Abandoned' ? 'border-l-4 border-l-amber-500' :
                    getGameStatus(game) === 'Completed' ? 'border-l-4 border-l-green-500' : ''
                  }`}>
                  {/* Main Game Info */}
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getGameStatus(game) === 'Scheduled' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Scheduled
                            </span>
                          )}
                          {getGameStatus(game) === 'Completed' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Completed
                            </span>
                          )}
                          {getGameStatus(game) === 'Abandoned' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              Abandoned
                            </span>
                          )}
                          {getGameStatus(game) === 'Cancelled' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Cancelled
                            </span>
                          )}
                        </div>
                        <h3 className={`font-bold text-base sm:text-lg ${getGameStatus(game) === 'Cancelled' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{game.name}</h3>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm text-gray-600">
                            📅 {formatDate(game.date, {
                              weekday: 'short',
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                            {game.time && ` at ${formatTime(game.time)} `}
                          </p>
                          {game.arrivalTime && (
                            <div className="mt-2 mb-2">
                              <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded border border-blue-200 font-medium">
                                ⏳ Arrival: {formatTime(game.arrivalTime)}
                                {game.lateFeeAmount && (
                                  <span className="ml-1 opacity-80 font-normal">
                                    (💲{game.lateFeeAmount} late fee after {game.lateFeeCutoffTime ? formatTime(game.lateFeeCutoffTime) : formatTime(game.arrivalTime)})
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                          {game.homeTeam && game.awayTeam && (
                            <p className="text-sm text-gray-600">
                              🏏 {game.homeTeam} vs {game.awayTeam}
                            </p>
                          )}
                          {game.opponent && !game.awayTeam && (
                            <p className="text-sm text-gray-600">🆚 {game.opponent}</p>
                          )}
                          {game.venue && (
                            <div className="flex items-center gap-2">
                              <p className="text-sm text-gray-600">📍 {game.venue}</p>
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(game.venue)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 text-xs font-semibold"
                                title="Open in Google Maps"
                              >
                                <MapPin size={14} />
                                Maps
                              </a >
                            </div >
                          )}
                          {
                            game.tournamentName && (
                              <p className="text-sm text-purple-600">🏆 {game.tournamentName}</p>
                            )
                          }

                          {/* Squad Status */}
                          <div className="flex items-center gap-3 mt-2">
                            <span className={`text-sm font-medium ${hasSquad ? 'text-green-600' : 'text-orange-600'}`}>
                              👥 Squad: {game.playing11?.length || 0}{maxSquadSize > 0 ? `/${maxSquadSize}` : ''}
                              {hasSquad && ' ✓'}
                            </span>
                            {game.superSubs?.length > 0 && (
                              <span className="text-sm text-purple-600 font-medium">
                                ⭐ {game.superSubs.length} super sub{game.superSubs.length !== 1 ? 's' : ''}
                              </span>
                            )}
                            {hasResponsibilities && (
                              <span className="text-sm text-orange-600 font-medium">
                                📋 Duties assigned
                              </span>
                            )}
                          </div>

                          {/* Availability Summary */}
                          {
                            game.availability && Object.keys(game.availability).length > 0 && (
                              <div className="text-sm mt-2 flex items-center gap-3">
                                <span className="text-gray-600 font-medium">Availability:</span>
                                <span className="text-green-600">
                                  ✓ {Object.values(game.availability).filter(a => a.status === 'available').length}
                                </span>
                                <span className="text-red-600">
                                  ✗ {Object.values(game.availability).filter(a => a.status === 'unavailable').length}
                                </span>
                                <span className="text-yellow-600">
                                  ? {Object.values(game.availability).filter(a => a.status === 'maybe').length}
                                </span>
                                {Object.values(game.availability).some(a => a.isChanged) && (
                                  <span className="text-xs bg-yellow-500 text-white px-2 py-1 rounded font-semibold animate-pulse">
                                    {Object.values(game.availability).filter(a => a.isChanged).length} CHANGED
                                  </span>
                                )}
                              </div>
                            )
                          }
                        </div >
                      </div >

                      {/* Match Recap Section */}
                      {(game.matchRecap || (game.memorablePerformances && game.memorablePerformances.length > 0)) && (
                        <div className="mb-4 mt-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                          <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2 text-sm">
                            <ClipboardList size={14} className="text-blue-600" />
                            Match Recap
                          </h4>
                          {game.matchRecap && (
                            <p className="text-xs text-gray-700 whitespace-pre-wrap mb-2">
                              {game.matchRecap}
                            </p>
                          )}

                          {game.memorablePerformances && game.memorablePerformances.length > 0 && (
                            <div className="space-y-1 mt-2 pt-2 border-t border-blue-100/50">
                              <h5 className="font-semibold text-[10px] text-gray-500 uppercase tracking-wider">Highlights</h5>
                              <ul className="space-y-1.5">
                                {game.memorablePerformances.map((perf, idx) => (
                                  <li key={idx} className="text-[11px] bg-white p-1.5 rounded border border-gray-100 flex flex-col gap-0.5">
                                    <span className="font-semibold text-gray-900">
                                      {Array.from(new Set(perf.playerIds.map(id => {
                                        const player = players.find(p => p.id === id || p.userId === id);
                                        return player?.name || 'Unknown Player';
                                      }))).join(', ')}
                                    </span>
                                    <span className="text-gray-600">{perf.description}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Media Gallery Preview */}
                      <MediaGalleryPreview gameId={game.id} />

                      <div className="flex flex-col gap-2">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => onEditGame(game)}
                            className="px-3 py-1.5 border rounded-lg text-xs sm:text-sm font-semibold border-blue-600 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setEditingRecapGame(game)}
                            className="px-3 py-1.5 border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-1"
                          >
                            📝 Recap
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setUploadingMediaGame(game); }}
                            className="px-3 py-1.5 border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-1"
                          >
                            <Camera size={14} />
                            Upload Media
                          </button>
                          <button
                            onClick={() => onSelectPlaying11(game.id)}
                            className="px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold text-white bg-green-600 hover:bg-green-700"
                          >
                            {hasSquad ? 'Edit Squad' : 'Select Squad'}
                          </button>
                          <button
                            disabled={!isAdmin && isPast}
                            onClick={() => {
                              if (confirm(`Delete "${game.name}"? This cannot be undone.`)) {
                                onDeleteGame(game.id);
                              }
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm ${!isAdmin && isPast ? 'text-gray-400 opacity-50 cursor-not-allowed' : 'text-red-600 hover:text-red-800 hover:bg-red-50'}`}
                          >
                            Delete
                          </button>
                        </div>

                        {/* Admin Game Status Override */}
                        <div className="mt-1">
                          <select
                            value={game.status || ''}
                            onChange={(e) => onUpdateGameStatus && onUpdateGameStatus(game.id, e.target.value)}
                            className={`w-full sm:w-auto px-3 py-1.5 text-xs sm:text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 font-medium ${!game.status ? 'bg-gray-50 text-gray-700' :
                              game.status === 'Cancelled' ? 'bg-red-50 border-red-200 text-red-900' :
                                game.status === 'Abandoned' ? 'bg-amber-50 border-amber-200 text-amber-900' :
                                  'bg-green-50 border-green-200 text-green-900'
                              }`}
                          >
                            <option value="">Status: Auto</option>
                            <option value="Completed">Status: Completed</option>
                            <option value="Abandoned">Status: Abandoned</option>
                            <option value="Cancelled">Status: Cancelled</option>
                          </select>
                        </div>

                        {/* Reminder Buttons */}
                        <div className="flex flex-wrap gap-2">
                          <button
                            disabled={!isAdmin && isPast}
                            onClick={() => onSendGameReminder(game.id)}
                            className={`px-2 py-1 rounded text-xs sm:text-sm font-semibold ${!isAdmin && isPast ? 'bg-gray-100 text-gray-400 opacity-50 cursor-not-allowed' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`}
                            title="Send game reminder to all players in both teams"
                          >
                            🔔 Reminder
                          </button>
                          <button
                            disabled={!isAdmin && isPast}
                            onClick={() => onSendAvailabilityReminder(game.id)}
                            className={`px-2 py-1 rounded text-xs sm:text-sm font-semibold ${!isAdmin && isPast ? 'bg-gray-100 text-gray-400 opacity-50 cursor-not-allowed' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'}`}
                            title="Remind players to provide availability"
                          >
                            ⏰ Availability
                          </button>
                          {game.availability && Object.keys(game.availability).length > 0 && (
                            <button
                              onClick={() => onViewAvailability && onViewAvailability(game)}
                              className="px-2 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded text-xs sm:text-sm font-semibold"
                              title="View who has responded"
                            >
                              📋 Responses
                            </button>
                          )}
                        </div>
                      </div>
                    </div >
                  </div >

                  {/* Expandable Squad Details */}
                  {
                    ((game.playing11?.length || 0) > 0) && (
                      <>
                        <button
                          onClick={() => setExpandedGame(isExpanded ? null : game.id)}
                          className="w-full px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
                        >
                          <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <Users size={16} />
                            View Squad & Responsibilities
                          </span>
                          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>

                        {isExpanded && (
                          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Playing Squad */}
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                  👥 Playing Squad ({game.playing11?.length || 0})
                                </h4>
                                <div className="space-y-2">
                                  {game.playing11?.map((playerId, index) => {
                                    const playerName = getPlayerName(playerId);
                                    const isSuperSub = game.superSubs?.includes(playerId);
                                    const isCaptain = game.captain === playerId;
                                    const isViceCaptain = game.viceCaptain === playerId;
                                    // Get player's duty - check both formats
                                    const playerDuty = game.playerDuties?.[playerId];
                                    const playerResps = game.playerResponsibilities?.[playerId] || [];
                                    // Combine into array for display
                                    const allDuties = playerDuty ? [playerDuty] : (Array.isArray(playerResps) ? playerResps : []);

                                    return (
                                      <div
                                        key={playerId}
                                        className={`flex items-center justify-between p-2 rounded-lg ${isSuperSub ? 'bg-purple-50' : 'bg-white'
                                          }`}
                                      >
                                        <div className="flex items-center gap-2">
                                          <span className="text-gray-400 text-sm w-6">{index + 1}.</span>
                                          <span className="font-medium">{playerName}</span>
                                          {isCaptain && (
                                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded font-semibold">C</span>
                                          )}
                                          {isViceCaptain && (
                                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-semibold">VC</span>
                                          )}
                                          {isSuperSub && (
                                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-semibold flex items-center gap-1">
                                              <Star size={10} fill="currentColor" />
                                              Super Sub
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex gap-1">
                                          {allDuties.map(respId => {
                                            const resp = RESPONSIBILITIES[respId];
                                            if (!resp) return null;
                                            const IconComponent = resp.icon;
                                            return (
                                              <span
                                                key={respId}
                                                className={`p-1 rounded ${resp.color === 'orange' ? 'bg-orange-100 text-orange-600' :
                                                  resp.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                                                    resp.color === 'green' ? 'bg-green-100 text-green-600' :
                                                      'bg-gray-100 text-gray-600'
                                                  }`}
                                                title={resp.label}
                                              >
                                                <IconComponent size={14} />
                                              </span>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Responsibilities Summary - Editable before game day */}
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                  📋 Match Responsibilities
                                  {(!isGameDayOrPast(game.date) || isAdmin) && onAssignDuty && (
                                    <span className="text-xs font-normal text-gray-500">(Click to assign)</span>
                                  )}
                                </h4>
                                <div className="space-y-3">
                                  {Object.entries(RESPONSIBILITIES).map(([respId, resp]) => {
                                    const assignedPlayers = getPlayersWithResponsibility(game, respId);
                                    const IconComponent = resp.icon;
                                    const isEditing = editingDuty?.gameId === game.id && editingDuty?.dutyId === respId;
                                    const canEdit = (!isGameDayOrPast(game.date) || isAdmin) && onAssignDuty;

                                    // Get current player assigned to this duty
                                    const currentPlayerId = Object.entries(game.playerDuties || {})
                                      .find(([pid, did]) => did === respId)?.[0];

                                    return (
                                      <div
                                        key={respId}
                                        className={`p-3 rounded-lg border-2 transition-all ${assignedPlayers.length > 0
                                          ? resp.color === 'orange' ? 'border-orange-200 bg-orange-50' :
                                            resp.color === 'blue' ? 'border-blue-200 bg-blue-50' :
                                              resp.color === 'green' ? 'border-green-200 bg-green-50' :
                                                'border-gray-200 bg-gray-50'
                                          : 'border-gray-200 bg-white'
                                          } ${canEdit ? 'cursor-pointer hover:border-purple-300 hover:shadow-sm' : ''}`}
                                        onClick={() => {
                                          if (canEdit && !isEditing) {
                                            setEditingDuty({ gameId: game.id, dutyId: respId });
                                          }
                                        }}
                                      >
                                        <div className="flex items-center gap-2 mb-1">
                                          <IconComponent size={16} className={
                                            resp.color === 'orange' ? 'text-orange-600' :
                                              resp.color === 'blue' ? 'text-blue-600' :
                                                resp.color === 'green' ? 'text-green-600' :
                                                  'text-gray-600'
                                          } />
                                          <span className="font-semibold text-gray-800">{resp.label}</span>
                                          {canEdit && !isEditing && (
                                            <span className="ml-auto text-xs text-purple-600">✏️ Edit</span>
                                          )}
                                        </div>

                                        {isEditing ? (
                                          <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                                            <select
                                              className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                              value={currentPlayerId || ''}
                                              onChange={(e) => {
                                                const newPlayerId = e.target.value;
                                                onAssignDuty(game.id, respId, newPlayerId || null);
                                                setEditingDuty(null);
                                              }}
                                              autoFocus
                                            >
                                              <option value="">-- Not assigned --</option>
                                              {game.playing11?.map(playerId => (
                                                <option key={playerId} value={playerId}>
                                                  {getPlayerName(playerId)}
                                                </option>
                                              ))}
                                            </select>
                                            <button
                                              onClick={() => setEditingDuty(null)}
                                              className="mt-2 text-xs text-gray-500 hover:text-gray-700"
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        ) : (
                                          <p className={`text-sm ${assignedPlayers.length > 0 ? 'text-gray-700' : 'text-gray-400 italic'
                                            }`}>
                                            {assignedPlayers.length > 0
                                              ? assignedPlayers.join(', ')
                                              : 'Not assigned'
                                            }
                                          </p>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>

                            {/* Late Arrivals Section - Only show on game day or after */}
                            {isGameDayOrPast(game.date) && game.playing11?.length > 0 && (
                              <div className="mt-6 pt-6 border-t border-gray-200">
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                  <Clock size={18} className="text-red-500" />
                                  Attendance - Mark Late Arrivals
                                  <span className="text-xs font-normal text-gray-500 ml-2">
                                    (+${game.lateFeeAmount ?? 5} fee per late arrival)
                                  </span>
                                </h4>

                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                  {game.playing11?.map((playerId) => {
                                    const playerName = getPlayerName(playerId);
                                    const isLate = game.lateArrivals?.[playerId];

                                    return (
                                      <button
                                        key={playerId}
                                        onClick={() => onMarkLateArrival && onMarkLateArrival(game.id, playerId, !isLate)}
                                        className={`p-3 rounded-lg border-2 text-left transition-all ${isLate
                                          ? 'border-red-300 bg-red-50 hover:bg-red-100'
                                          : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
                                          }`}
                                      >
                                        <div className="flex items-center justify-between">
                                          <span className={`font-medium text-sm ${isLate ? 'text-red-700' : 'text-gray-700'}`}>
                                            {playerName}
                                          </span>
                                          {isLate ? (
                                            <span className="flex items-center gap-1 text-red-600">
                                              <AlertCircle size={14} />
                                              <span className="text-xs font-semibold">LATE</span>
                                            </span>
                                          ) : (
                                            <span className="text-green-600 text-xs">✓ On time</span>
                                          )}
                                        </div>
                                        {isLate && (
                                          <p className="text-xs text-red-500 mt-1">
                                            +${game.lateFeeAmount ?? 5} fee added
                                          </p>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>

                                {/* Late arrivals summary */}
                                {game.lateArrivals && Object.keys(game.lateArrivals).length > 0 && (
                                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-700">
                                      <strong>{Object.keys(game.lateArrivals).length}</strong> player(s) marked late •
                                      Total late fees: <strong>${Object.keys(game.lateArrivals).length * (game.lateFeeAmount ?? 5)}</strong>
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )
                  }
                </div >
              );
            }; // End renderGameCard

            return Object.entries(gamesByTournament).map(([tournamentName, { active, completed }]) => {
              const totalGames = active.length + completed.length;
              return (
                <div key={tournamentName} className="mb-8 last:mb-0">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-gray-100 flex items-center gap-2">
                    {tournamentName === 'Independent Games' ? '🏏' : '🏆'} {tournamentName}
                    <span className="text-sm font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full ml-2">
                      {totalGames} game{totalGames !== 1 ? 's' : ''}
                    </span>
                  </h3>

                  {active.length > 0 && (
                    <div className="space-y-4">
                      {active.map(game => renderGameCard(game))}
                    </div>
                  )}

                  {completed.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-lg font-semibold text-gray-600 mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                        Completed Games
                        <span className="text-xs font-normal bg-gray-100 px-2 py-0.5 rounded-full">{completed.length}</span>
                      </h4>
                      <div className="space-y-4">
                        {completed.map(game => renderGameCard(game))}
                      </div>
                    </div>
                  )}
                </div>
              );
            });
          })()
        )}
      </div >
      <ImportScheduleModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        teams={teams || []}
        tournaments={tournaments || []}
        onImportGames={onImportGames}
      />

      <MatchRecapModal
        isOpen={!!editingRecapGame}
        game={editingRecapGame || {}}
        players={players}
        onClose={() => setEditingRecapGame(null)}
        onSave={async (recapData) => {
          if (onSaveMatchRecap) {
            await onSaveMatchRecap(editingRecapGame.id, recapData);
          }
          setEditingRecapGame(null);
        }}
      />

      <MediaUploadModal
        isOpen={!!uploadingMediaGame}
        onClose={() => setUploadingMediaGame(null)}
        game={uploadingMediaGame || {}}
        currentUser={{ id: 'admin', name: `Admin: ${getPlayerName('admin')}` }}
      />
    </div >
  );
};

export default GamesList;